// src/hooks/useDietaryAnalysis.ts

import { useState, useEffect, useCallback } from 'react';
import { buildAssessment, Assessment } from '@/services/dietaryService';
import { RecipeFormData } from '@/types/recipe';

export const useDietaryAnalysis = (recipe: RecipeFormData | null) => {
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const runAnalysis = useCallback(async () => {
    if (!recipe?.ingredients) {
      setAssessment(null);
      return;
    }
    setIsLoading(true);
    try {
      // Convert ingredients from string to array
      const ingredientsArray = recipe.ingredients.split('\n').filter((line: string) => line.trim());
      // Convert method from string to array if it exists
      const methodArray = recipe.method ? recipe.method.split('\n').filter((line: string) => line.trim()) : [];
      
      const result = await buildAssessment(ingredientsArray, methodArray, 1);
      setAssessment(result);
    } catch {
      setAssessment(null);
    }
    setIsLoading(false);
  }, [recipe?.ingredients, recipe?.method]);

  useEffect(() => {
    // Debounce the analysis to avoid excessive API calls during typing
    const handler = setTimeout(() => {
      runAnalysis();
    }, 500); // 500ms delay

    return () => {
      clearTimeout(handler);
    };
  }, [runAnalysis]);

  return { assessment, isLoading, refresh: runAnalysis };
};